import { Typography } from 'antd';
import { useMemo } from 'react';

import {
  TemplateApplyingDialog,
  TemplateCreationDialog,
  TemplateDeletionDialog,
  Templates,
  AccessRightsList,
} from '@app/pages/Users/AccessRightsTab/components';
import {
  useAccessRightsTabEffects,
  useAccessRightsList,
  useAccessRightsTemplates,
} from '@app/pages/Users/AccessRightsTab/hooks';
import type { AccessRightsTabProps } from '@app/pages/Users/AccessRightsTab/types';

import '@app/pages/Users/AccessRightsTab/styles/AccessRightsTab.desktop.scss';

const { Title } = Typography;

export const AccessRightsTab = ({
  editableUserAccessRights,
  editableUserUnsavedAccessRights,
  setEditableUserUnsavedAccessRights,
  isGetUserAccessRightsLoading,
  setEditableUserAccessRights,
}: AccessRightsTabProps) => {
  const {
    isAdmin,
    isDesktop,
    showCreateTemplateButton,
    hasChanges,
    onChangeEditableUserUnsavedAccessRights,
    checkHasChanges,
    updateUserAccessRightsHandler,
    isUpdateUserAccessRightsLoading,
    formattedAccordionList,
    isModulesRightsFetching,
    isModulesRightsLoading,
  } = useAccessRightsList({
    editableUserAccessRights,
    editableUserUnsavedAccessRights,
    setEditableUserUnsavedAccessRights,
    setEditableUserAccessRights,
  });

  const {
    showTemplates,
    getAccessRightsTemplatesHandler,
    templates,
    isTemplatesLoading,
    onSelectTemplate,
    onDeleteTemplate,
    showDeleteTemplateButton,
    isTemplateCreationDialog,
    isTemplateApplyingDialog,
    isTemplateDeletionDialog,
    selectedTemplate,
    isApplyAccessRightsTemplateLoading,
    isDeleteAccessRightsTemplateLoading,
    isUpdateAccessRightsTemplateLoading,
    applyAccessRightsTemplateHandler,
    deleteAccessRightsTemplateHandler,
    createAccessRightsTemplateHandler,
    onCancelTemplateApplyingModal,
    onCancelTemplateDeletionModal,
    onCancelTemplateCreationModal,
    openCreateTemplateDialog,
  } = useAccessRightsTemplates({
    editableUserAccessRights,
    onChangeEditableUserUnsavedAccessRights,
    editableUserUnsavedAccessRights,
  });

  useAccessRightsTabEffects({
    getAccessRightsTemplatesHandler,
    checkHasChanges,
  });

  const isPageLoading = useMemo(() => {
    return (
      isModulesRightsFetching ||
      isModulesRightsLoading ||
      isGetUserAccessRightsLoading
    );
  }, [
    isModulesRightsFetching,
    isModulesRightsLoading,
    isGetUserAccessRightsLoading,
  ]);

  return (
    <div className="access-rights">
      <Templates
        showTemplates={showTemplates}
        isLoading={isTemplatesLoading}
        templates={templates}
        onSelectTemplate={onSelectTemplate}
        onDeleteTemplate={onDeleteTemplate}
        showDeleteTemplateButton={showDeleteTemplateButton}
      />

      <Title level={3} data-testid="access-rights">
        Права доступа
      </Title>

      <AccessRightsList
        accordionList={formattedAccordionList}
        unsavedAccessRights={editableUserUnsavedAccessRights}
        isLoading={isPageLoading}
        isSaveLoading={isUpdateUserAccessRightsLoading}
        isAdmin={isAdmin}
        isDesktop={isDesktop}
        hasChanges={hasChanges}
        showCreateTemplateButton={showCreateTemplateButton}
        openCreateTemplateDialog={openCreateTemplateDialog}
        onSave={updateUserAccessRightsHandler}
      />

      <TemplateApplyingDialog
        selectedTemplate={selectedTemplate}
        onOk={applyAccessRightsTemplateHandler}
        onCancel={onCancelTemplateApplyingModal}
        isOpened={isTemplateApplyingDialog}
        isLoading={isApplyAccessRightsTemplateLoading}
      />

      <TemplateDeletionDialog
        onOk={deleteAccessRightsTemplateHandler}
        onCancel={onCancelTemplateDeletionModal}
        isOpened={isTemplateDeletionDialog}
        isLoading={isDeleteAccessRightsTemplateLoading}
      />

      <TemplateCreationDialog
        onFinish={createAccessRightsTemplateHandler}
        onCancel={onCancelTemplateCreationModal}
        isOpened={isTemplateCreationDialog}
        isLoading={isUpdateAccessRightsTemplateLoading}
      />
    </div>
  );
};
